import { Trans, useTranslation } from "react-i18next";
import emotionStyled from "@emotion/styled";
import { TextV2 } from "src/components/TextV2/TextV2.tsx";
import { H5, H6 } from "src/components/HeadingV2/HeadingV2.tsx";
import { Flex } from "src/components/Flex.tsx";
import { TooltipV2 } from "src/components/TooltipV2/TooltipV2.tsx";
import { IconButtonV2 } from "src/components/IconButtonV2/IconButtonV2.tsx";
import { Counter } from "src/components/Counter/Counter.tsx";
import * as styles from "./ProductCard.styles.ts";
import { TranslationNamespaceEnum } from "src/lib/i18next/types.ts";
import { keyframes } from "@emotion/react";
import { formatPercents, formatPrice, formatWeight } from "@exonn/utils";
import { Currency } from "src/common/constants.ts";
import { type MouseEventHandler } from "react";
// import { Badge } from "../Badge/Badge.tsx";
import { ShowMore } from "@re-dev/react-truncate";
import { useExonnHubStore } from "src/admin/modules/b2b-gate-order/components/ExonnHub/useExonnHubStore.ts";
import { observer } from "mobx-react-lite";
import { useExonnHubOrder } from "src/admin/modules/b2b-gate-order/components/ExonnHub/hooks/useExonnHubOrder.ts";
import { useAddProductToFavorite } from "src/admin/modules/b2b-gate-order/components/ExonnHub/hooks/useAddProductToFavorite.ts";
import { useRemoveProductFromFavourite } from "src/admin/modules/b2b-gate-order/components/ExonnHub/hooks/useRemoveProductFromFavourite.ts";
import { useExonnHubCatalogParams } from "src/admin/modules/b2b-gate-order/components/ExonnHub/hooks/useExonnHubCatalogParams.ts";
import { useExonnHubCatalog } from "src/admin/modules/b2b-gate-order/components/ExonnHub/hooks/useExonnHubCatalog.tsx";
import { keepPreviousData } from "@tanstack/react-query";
import { useExonnHubContext } from "src/admin/modules/b2b-gate-order/components/ExonnHub/useExonnHubContext.ts";
import type { ExonnHubProduct as BackendExonnHubProduct } from "@exonn/js-sdk";
import placeholderImage from "src/assets/illustrations/placeholder_image.svg";
import { Link } from "src/components/TextV2/Link.tsx";
import { useIsDemoShop } from "src/hooks/use-is-demo-shop.ts";
import { useAddProductToOrder } from "src/admin/modules/b2b-gate-order/components/EditSupplierOrderForB2bGate/hooks/useAddProductToCart.ts";

type ProductCardProps = { data: BackendExonnHubProduct };

export const ProductCard = observer((props: ProductCardProps) => {
  const { data: product } = props;
  const { orderId } = useExonnHubContext();
  const { data: cartModel } = useExonnHubOrder({
    orderId,
  });
  const isDemoShop = useIsDemoShop();
  const store = useExonnHubStore();
  const isFilterPanelOpen = store.isFilterPanelOpen;
  const isFullscreen = store.isFullscreen;
  const amountPerPackage = +product.amount_of_unit_per_package;
  const unitPrice = +product.unit_price;
  const unitNetWeight = +product.unit_net_weight;
  const productNumber = product.sku;
  const supplier = product.supplier.company;
  const saleMinQuantity = 1;
  const saleMaxQuantity = Math.floor(+product.stock / amountPerPackage);

  const listParams = useExonnHubCatalogParams();

  const { data: catalogData } = useExonnHubCatalog(listParams, {
    placeholderData: keepPreviousData,
  });

  const favoriteProductIds = (catalogData?.data || [])
    .filter((product) => product.is_favourite)
    .map((product) => product.id);

  const { mutate: addProductToFavorite } = useAddProductToFavorite(listParams);

  const { mutate: removeProductToFavorite } =
    useRemoveProductFromFavourite(listParams);

  const { mutate: updateProductInOrder } = useAddProductToOrder();

  const cartItem = (cartModel?.items || []).find(
    (cartItem) => cartItem.productId === product.id,
  );

  const onCartQuantityChange = (quantityInPackages: number) => {
    if (cartItem) {
      updateProductInOrder({
        data: {
          b2b_gate_order_id: orderId,
          quantity_ordered: quantityInPackages * amountPerPackage,
          variant_id: product.id,
          unit_price: unitPrice,
        },
        locale: "de",
      });
    } else {
      updateProductInOrder({
        data: {
          b2b_gate_order_id: orderId,
          quantity_ordered: amountPerPackage,
          variant_id: product.id,
          unit_price: unitPrice,
        },
        locale: "de",
      });
    }
  };

  const handleProductCardClick = () => {
    store.toggleProduct(product);
  };

  const isFavorite = favoriteProductIds.includes(product.id);

  const quantityOrderedInPackages = cartItem?.quantityInPackages ?? 0;
  const openedProduct = store.openedProduct;

  const isPreviewOpen = store.isProductPanelOpen;

  const { t } = useTranslation(TranslationNamespaceEnum.ExonnHubComponent);

  const handleLikeButtonClick: MouseEventHandler<HTMLButtonElement> = (e) => {
    e.stopPropagation();
    if (isFavorite) {
      removeProductToFavorite({
        variant_id: product.id,
      });
    } else {
      addProductToFavorite({
        variant_id: product.id,
      });
    }
  };

  const handleAddToCartButtonClick: MouseEventHandler<HTMLButtonElement> = (
    e,
  ) => {
    e.stopPropagation();
    onCartQuantityChange?.(1);
  };

  const handleRemoveFromCartButtonClick = () => {
    onCartQuantityChange?.(0);
  };

  const properties = [
    {
      label: t("previewPanel.properties.weight"),
      value: formatWeight(unitNetWeight, product.unit),
    },
    {
      label: t("previewPanel.properties.vatPercent"),
      value: formatPercents(+product.vat_percent),
    },
    {
      label: (
        <TooltipV2 content="Verpackungseinheit">
          {t("amountPerPackage")}
        </TooltipV2>
      ),
      value: amountPerPackage + " " + t("pc"),
    },
    {
      label: t("pricePerUnit"),
      value: isDemoShop ? (
        <Link
          variant={"body1"}
          style={{
            textDecoration: "none",
          }}
          onClick={(e) => {
            e.stopPropagation();
          }}
          onPointerEnter={(e) => {
            store.drawAttentionToWarning({
              intensifyPulsation: 4000,
              shake: 750,
            });
          }}
        >
          <span className={"icon-15-info"} />
        </Link>
      ) : (
        formatPrice(unitPrice, Currency.EUR)
      ),
    },
  ];

  const isOpen = openedProduct?.id === product.id;

  return (
    <div
      onClick={handleProductCardClick}
      css={styles.root({
        isAddedToCart: quantityOrderedInPackages > 0,
        isOpen: isOpen,
      })}
    >
      <div css={styles.image}>
        <div css={styles.productNumber}>
          <TextV2 variant={"body2"} fontWeight={"medium"}>
            {t("productCard.productNumber")}:
          </TextV2>{" "}
          <TextV2 variant={"body2"} fontWeight={"normal"}>
            {" "}
            {productNumber}
          </TextV2>
        </div>
        <div
          css={styles.productImage({
            src: product.images?.[0]?.url ?? placeholderImage,
            isPlaceholder: !product.images?.[0],
          })}
        />
      </div>
      <div css={styles.description}>
        <H5 css={styles.title}>
          <ShowMore more={""} lines={2}>
            {product.title}
          </ShowMore>
        </H5>
        <H6 css={styles.supplierTitle}>{supplier}</H6>
        <Flex
          style={{
            marginTop: "5px",
            position: "absolute",
            bottom: 10,
          }}
        >
          <Link>
            {isOpen && <span className={"icon-15-sb_arw_mn"} />}
            {isOpen ? " " + t("lessInformation") : t("moreInformation") + " "}
            {!isOpen && <span className={"icon-15-sb_arw_mx"} />}
          </Link>
        </Flex>
      </div>
      <div
        css={styles.specs({ isPreviewOpen, isFilterPanelOpen, isFullscreen })}
      >
        <dl css={styles.specsContainer}>
          {properties.map((definition, index) => (
            <div key={index} css={styles.specItem}>
              <dt>
                <TextV2 variant={"body1"}>{definition.label}</TextV2>
              </dt>
              <dd
                style={{
                  marginLeft: 10,
                }}
              >
                <TextV2 variant={"body1"}>{definition.value}</TextV2>
              </dd>
            </div>
          ))}
        </dl>
      </div>
      <div css={styles.actions}>
        <div css={styles.likeContainer}>
          <TooltipV2
            content={
              isFavorite ? t("removeFromFavorites") : t("addToFavorites")
            }
          >
            <button
              onClick={handleLikeButtonClick}
              css={styles.likeButton({ liked: isFavorite })}
            >
              {isFavorite ? (
                <i className={"icon-fl-15-heart_f"} />
              ) : (
                <i className={"icon-15-heart"} />
              )}
            </button>
          </TooltipV2>
        </div>

        {quantityOrderedInPackages === 0 ? (
          <div css={styles.addToCartButton}>
            <IconButtonV2
              onClick={handleAddToCartButtonClick}
              size={"sm"}
              skin={"info"}
              disabled={isDemoShop}
            >
              <span className={"icon-15-add"} />
            </IconButtonV2>
          </div>
        ) : (
          <div css={styles.counter}>
            <Counter
              min={saleMinQuantity}
              max={saleMaxQuantity}
              onDelete={handleRemoveFromCartButtonClick}
              count={quantityOrderedInPackages}
              onChange={onCartQuantityChange}
            />
          </div>
        )}
        {quantityOrderedInPackages > 0 && cartItem && (
          <div css={styles.totalWrapper}>
            <TextV2 variant={"body2"} css={styles.totalExplanation}>
              <Trans
                i18nKey="totalExplanation"
                t={t}
                values={{
                  packages: quantityOrderedInPackages,
                  units: quantityOrderedInPackages * amountPerPackage,
                }}
              />
              {/*{formatPrice(unitPrice, Currency.EUR)}*/}
            </TextV2>
            <H6 css={styles.total}>
              {" "}
              {formatPrice(cartItem.total, Currency.EUR)}
            </H6>
          </div>
        )}
      </div>

      {/*<div css={styles.badges({ isPreviewOpen })}>*/}
      {/*  {(data.badges || []).map((badge, index) => (*/}
      {/*    <Badge key={index} color={badge.color}>*/}
      {/*      {badge.label}*/}
      {/*    </Badge>*/}
      {/*  ))}*/}
      {/*</div>*/}
    </div>
  );
});

const spin = keyframes`
    from {
        transform: rotate(0deg);
    }
    to {
        transform: rotate(360deg);
    }
`;

const Spinner = emotionStyled.i`
  animation: ${spin} 1s linear infinite;
`;

Spinner.defaultProps = {
  className: "fi fi-rs-spinner",
};
